import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Box,
  Alert,
  Avatar,
  Stack,
} from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { FiArrowRight } from 'react-icons/fi';
import { CohortStudent, CohortWithDetails } from 'src/types/cohort.types';

interface MoveStudentModalProps {
  open: boolean;
  onClose: () => void;
  student: CohortStudent | null;
  currentCohort: CohortWithDetails | null;
  availableCohorts: CohortWithDetails[];
  onConfirmMove: (studentId: string, fromCohortId: string, toCohortId: string) => Promise<void>;
  isSubmitting?: boolean;
}

export const MoveStudentModal: React.FC<MoveStudentModalProps> = ({
  open,
  onClose,
  student,
  currentCohort,
  availableCohorts,
  onConfirmMove,
  isSubmitting = false,
}) => {
  const [targetCohortId, setTargetCohortId] = useState('');
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    setTargetCohortId('');
    setError(null);
  }, [student, open]);

  if (!student || !currentCohort) return null;

  const validTargetCohorts = availableCohorts.filter((c) => c.id !== currentCohort.id);
  const targetCohort = availableCohorts.find((c) => c.id === targetCohortId);

  const handleMove = async () => {
    if (!targetCohortId) {
      setError('Please select a destination cohort.');
      return;
    }

    try {
      setError(null);
      await onConfirmMove(student.studentId, currentCohort.id, targetCohortId);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to move student.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Transfer Scholar Cohort
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Reassign scholar to a new cohort section
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <IoClose />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ p: 2, bg: '#F7FAFC', borderRadius: 2, border: '1px solid #E2E8F0', mb: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#EDB42E', color: '#000', fontWeight: 700 }}>
              {student.firstname?.[0] || 'S'}
            </Avatar>
            <Box>
              <Typography fontWeight={700} variant="subtitle2">
                {student.firstname} {student.lastname}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {student.studentId.substring(0, 8)}... | Grade {student.grade}
              </Typography>
              <Typography variant="caption" display="block" color="#2F855A" fontWeight={700}>
                Current Balance: ${student.balance?.toLocaleString() || 0}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5, px: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Current Cohort</Typography>
            <Typography variant="subtitle1" fontWeight={700} color="#3182CE">
              {currentCohort.name}
            </Typography>
          </Box>
          <FiArrowRight size={20} color="#A0AEC0" />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">New Cohort</Typography>
            <Typography variant="subtitle1" fontWeight={700} color={targetCohort ? '#2F855A' : '#A0AEC0'}>
              {targetCohort ? targetCohort.name : 'Select below'}
            </Typography>
          </Box>
        </Stack>

        <FormControl fullWidth size="small" required>
          <InputLabel id="target-cohort-label">Select Destination Cohort</InputLabel>
          <Select
            labelId="target-cohort-label"
            value={targetCohortId}
            label="Select Destination Cohort"
            onChange={(e) => setTargetCohortId(e.target.value)}
          >
            {validTargetCohorts.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.className ? `${c.className} - ` : ''}{c.name} ({c.students?.length || 0} scholars)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Alert severity="info" sx={{ mt: 2.5, fontSize: '0.75rem' }}>
          <strong>Data Safety Guaranteed:</strong> Scholar dollars, loan records, marketplace purchases, and street cred stay attached to the student. Changing cohorts will <strong>not</strong> reset their money or activity history.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleMove}
          variant="contained"
          disabled={isSubmitting || !targetCohortId}
          sx={{
            backgroundColor: '#EDB42E',
            color: '#000',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#d9a224' },
          }}
        >
          {isSubmitting ? 'Transferring...' : 'Confirm Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
